import { useEffect, useState } from 'react'
import workflowDefinitionsAPI, {
  WorkflowDefinition,
  WorkflowDefinitionEditableFields,
} from '~/api/desktop/workflowDefinitions'
import Dialog from '~/components/Dialog'
import StructuredDataForm from '~/components/StructuredDataForm'
import Toaster from '~/components/Toaster'
import { JsonSchema } from '~/types/JsonSchema.interface'
import cs from './workflow_definition_create_or_edit_dialog.scss'

interface WorkflowDefinitionCreateOrEditDialogProps {
  isOpen: boolean
  selectedWorkflowDefinition?: WorkflowDefinition
  onClose: () => void
  onCreateSuccess: () => void
}

const WorkflowDefinitionCreateOrEditDialog = ({
  isOpen,
  selectedWorkflowDefinition,
  onCreateSuccess,
  onClose,
}: WorkflowDefinitionCreateOrEditDialogProps) => {
  const [workflowDefinitionParams, setWorkflowDefinitionParams] =
    useState<Partial<WorkflowDefinitionEditableFields> | null>(null)
  const [schema, setSchema] = useState<JsonSchema | undefined>(undefined)

  const isEditExisting = !!selectedWorkflowDefinition

  const initialize = async () => {
    const response = await workflowDefinitionsAPI.schema()

    setSchema(response.json_schema as unknown as JsonSchema)
  }

  const resetFields = () => {
    if (!selectedWorkflowDefinition) {
      setWorkflowDefinitionParams({
        name: '',
        definition_file: '',
      })
    } else {
      setWorkflowDefinitionParams({
        name: selectedWorkflowDefinition.name,
        definition_file: selectedWorkflowDefinition.definition_file,
      })
    }
  }

  useEffect(() => {
    resetFields()
  }, [isOpen, selectedWorkflowDefinition])

  useEffect(() => {
    initialize()
  }, [])

  const isValid = () => {
    return (
      workflowDefinitionParams !== null &&
      !!workflowDefinitionParams.name &&
      !!workflowDefinitionParams.definition_file
    )
  }

  const handleConfigUpdateUi = (key: string, newValue: unknown) => {
    setWorkflowDefinitionParams({
      ...(workflowDefinitionParams ?? {}),
      [key]: newValue,
    })
  }

  const handleSave = async () => {
    if (!isValid()) return
    onClose()
    try {
      if (isEditExisting) {
        if (!selectedWorkflowDefinition) return
        await workflowDefinitionsAPI.update(
          selectedWorkflowDefinition.db_id,
          workflowDefinitionParams as WorkflowDefinitionEditableFields,
        )
      } else {
        await workflowDefinitionsAPI.create(
          workflowDefinitionParams as WorkflowDefinitionEditableFields,
        )
      }
      onCreateSuccess()
      Toaster.show({
        message: `Successfully ${
          isEditExisting ? 'updated' : 'created'
        } workflow definition`,
        intent: 'success',
      })
    } catch (_e) {
      Toaster.show({
        message: `Failed to ${
          isEditExisting ? 'update' : 'create'
        } workflow definition`,
        intent: 'danger',
      })
    }
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      className={cs.workflowDefinitionCreateOrEditDialog}
    >
      <Dialog.Title>
        {isEditExisting ? 'Edit' : 'Create'} Workflow Definition
      </Dialog.Title>
      <StructuredDataForm
        data={workflowDefinitionParams}
        variant='wideValues'
        onEdit={handleConfigUpdateUi}
        dataSchema={schema}
        className={cs.structuredDataForm}
      />
      <Dialog.Footer>
        <Dialog.FooterButton label='Cancel' onClick={onClose} />
        <Dialog.FooterButton
          type='primary'
          label='Save Changes'
          onClick={handleSave}
          disabled={!isValid()}
        />
        <div className={cs.fill} />
      </Dialog.Footer>
    </Dialog>
  )
}

export default WorkflowDefinitionCreateOrEditDialog
